import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import routes from '../../../constants/routes';
import {
  Wrapper,
  SecondaryTitle,
  StyledHeader,
  StyledTextField,
  SubHeader,
  SubText
} from '../styled';
import { Loader } from '../../common/styled/loader';
import etherLogo from '../../../assets/images/ether_logo.png';
import { getFooterLinks } from '../../HomePage/constants/login';
import AdminAuthContainer from '../AdminAuthContainer';
import loginAPi from '../../../apis/login';
import { useAuthRedirect } from '../utils/LoginAuthGuard';
import browserUtils from '../../../utils/browserUtils';

const validationSchema = yup.object({
  email: yup
    .string()
    .email("That doesn't look like an email address. Please try again.")
    .required('Email is required')
});

const AdminLogin = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isAuthCheckInProgress = useAuthRedirect();

  useEffect(() => {
    typeof window !== 'undefined' &&
      window.localStorage.removeItem('admin_email');
    browserUtils.stopBrowserBack(() => {});
    return () => {
      browserUtils.enableBrowserBack();
    };
  }, []);

  const isAdminLogin = true;

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      const email = values.email.toLowerCase();
      setIsLoading(true);
      try {
        await loginAPi.doLogin(email, isAdminLogin);
        window.localStorage.setItem('admin_email', email);
        navigate(routes.ADMIN_LOGIN_OTP);
      } catch (error) {
        setErrorMessage('Login failed. Please check your email and try again.');
      } finally {
        setIsLoading(false);
      }
    }
  });

  const isFormDisabled = !(formik.isValid && formik.dirty);
  const errorText =
    formik.touched.email && (formik.errors.email || errorMessage);
  const hasError = Boolean(errorText);

  return isAuthCheckInProgress ? (
    <Wrapper>
      <Loader size='2rem' />
    </Wrapper>
  ) : (
    <AdminAuthContainer
      logoSrc={String(etherLogo)}
      sections={[
        { title: 'Welcome to Ether!', titleStyle: StyledHeader },
        {
          subtext: 'Please sign in to manage your school administration.',
          subtextStyle: SubText
        },
        { title: 'Enter your email address', titleStyle: SecondaryTitle },
        {
          subtext: (
            <>
              A one-time password (OTP) will be sent to your email. <br />
              Use it to log in.
            </>
          ),
          subtextStyle: SubText
        },
        { title: 'Login', titleStyle: SubHeader }
      ]}
      buttonText='Send OTP now'
      onSubmit={formik.handleSubmit}
      footerText='By proceeding, I agree to the '
      footerLinks={getFooterLinks(navigate)}
      isLoading={isLoading}
      isButtonDisabled={isFormDisabled}
    >
      <StyledTextField
        label='Email ID'
        placeholder='Enter your email'
        required
        InputLabelProps={{ shrink: true }}
        onChange={formik.handleChange}
        name='email'
        value={formik.values.email}
        onBlur={formik.handleBlur}
        error={hasError}
        helperText={errorText}
      />
    </AdminAuthContainer>
  );
};

export default AdminLogin;
